import { useCallback, useEffect, useState } from "react";
import * as purchaseApi from "@/lib/purchaseApi";
import * as inventoryApi from "@/lib/inventoryApi";

const detail = error =>
  error?.response?.data?.detail?.message ||
  (typeof error?.response?.data?.detail === "string" ? error.response.data.detail : null) ||
  // Pydantic returns a list of field errors; show the first rather than "[object Object]".
  error?.response?.data?.detail?.[0]?.msg ||
  error?.message ||
  "Could not reach the server";

/**
 * Not every 409 is a stale version. `purchase_service._check_if_match` is the only
 * one that answers with `current_version`; a duplicate supplier name is a 409 too,
 * and telling the operator their order changed on another terminal would be a lie.
 */
const isVersionConflict = error =>
  error?.response?.status === 409 &&
  error?.response?.data?.detail?.current_version !== undefined;

/**
 * A stale If-Match means someone else placed, received or cancelled this order
 * first. Same treatment useOrder.js gives an order: say so plainly and re-read, so
 * the next attempt carries the current version.
 */
const message = error =>
  isVersionConflict(error)
    ? "This purchase order changed on another terminal. Reloaded — please redo that step."
    : detail(error);

/**
 * Suppliers, purchase orders, receiving, payments and payables against
 * /api/purchase. Admin-only throughout: a purchase order commits the restaurant's
 * money and receiving one moves stock.
 *
 * Totals and status never come from here — the server computes both from the lines
 * and the receipts, and this hook only ever shows what it returned.
 *
 * `enabled` is the role gate: a non-admin must fire no request at all rather than
 * collect 403s to render a disabled button.
 */
export function usePurchasing(token, enabled = true, { money = true } = {}) {
  const [suppliers, setSuppliers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [payments, setPayments] = useState([]);
  const [payables, setPayables] = useState([]);
  const [stockItems, setStockItems] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [returns, setReturns] = useState([]);
  const [status, setStatus] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [kind, setKind] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    if (!token || !enabled) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const results = await Promise.allSettled([
      // Inactive suppliers included: they still carry history and payables.
      purchaseApi.listSuppliers(false, token),
      purchaseApi.listOrders({ status: status || null, departmentId: departmentId || null, kind: kind || null,
                               limit: 100 }, token),
      // Supplier money is for someone who buys for every department (2026-11-01): a
      // department buyer asks for none of it rather than collecting 403s.
      money ? purchaseApi.listPayments({ limit: 100 }, token) : Promise.resolve([]),
      money ? purchaseApi.listPayables(token) : Promise.resolve([]),
      inventoryApi.listItems({ activeOnly: true }, token),
      purchaseApi.listDepartments(token),
      purchaseApi.listReturns({ limit: 100 }, token),
    ]);
    const [supplierRows, orderRows, paymentRows, payableRows, itemRows, deptRows, returnRows] = results;
    if (supplierRows.status === "fulfilled") setSuppliers(supplierRows.value);
    if (orderRows.status === "fulfilled") setOrders(orderRows.value);
    if (paymentRows.status === "fulfilled") setPayments(paymentRows.value);
    if (payableRows.status === "fulfilled") setPayables(payableRows.value);
    if (itemRows.status === "fulfilled") setStockItems(itemRows.value);
    if (deptRows.status === "fulfilled") setDepartments(deptRows.value);
    if (returnRows.status === "fulfilled") setReturns(returnRows.value);
    setLoaded(orderRows.status === "fulfilled");
    const failed = results.find(r => r.status === "rejected");
    setError(failed ? message(failed.reason) : null);
    setLoading(false);
  }, [token, enabled, status, departmentId, kind, money]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Only `ok` once the server confirmed and the page re-read. Any 409 also reloads —
   * the local copy lost a race, and the operator's next attempt would otherwise carry
   * the same stale version.
   */
  const guarded = fn => async (...args) => {
    try {
      const data = await fn(...args);
      await load();
      return { ok: true, data };
    } catch (caught) {
      if (caught?.response?.status === 409) await load();
      // `detail` rides along for a caller that acts on it (an overpayment's
      // advance_needed, owner brief B4).
      return { ok: false, error: message(caught), detail: caught?.response?.data?.detail };
    }
  };

  return {
    suppliers,
    orders,
    payments,
    payables,
    stockItems,
    departments,
    returns,
    status,
    departmentId,
    kind,
    setDepartmentId,
    setKind,
    error,
    loading,
    loaded,
    setStatus,
    reload: load,

    addSupplier: guarded(body => purchaseApi.createSupplier(body, token)),
    editSupplier: guarded((supplierId, patch) =>
      purchaseApi.updateSupplier(supplierId, patch, token)
    ),

    /** Body carries lines only — no total, no status. */
    addOrder: guarded(body => purchaseApi.createOrder(body, token)),
    placeOrder: guarded((poId, version) => purchaseApi.placeOrder(poId, version, token)),
    receiveOrder: guarded((poId, lines, version) =>
      purchaseApi.receiveOrder(poId, lines, version, token)
    ),
    cancelOrder: guarded((poId, version) => purchaseApi.cancelOrder(poId, version, token)),

    addPayment: guarded(body => purchaseApi.createPayment(body, token)),
    voidPayment: guarded((paymentId, reason) => purchaseApi.voidPayment(paymentId, reason, token)),
    voidBill: guarded((poId, reason, version) => purchaseApi.voidBill(poId, reason, version, token)),

    // Purchase completion (2026-11-01).
    editOrder: guarded((poId, patch, version) => purchaseApi.updateOrder(poId, patch, version, token)),
    addDirect: guarded(body => purchaseApi.createDirect(body, token)),
    addReturn: guarded(body => purchaseApi.createReturn(body, token)),
    addDepartment: guarded(body => purchaseApi.createDepartment(body, token)),
    editDepartment: guarded((deptId, patch) => purchaseApi.updateDepartment(deptId, patch, token)),
    // Files go up one by one after the record exists; each says how it went.
    uploadAttachment: guarded((parent, file) => purchaseApi.uploadAttachment(parent, file, token)),
    deleteAttachment: guarded(attachmentId => purchaseApi.deleteAttachment(attachmentId, token)),
  };
}
